import dynamics from "dynamics.js";
import _ from "lodash";
import memoizeOne from "memoize-one";
// import ImmutablePropTypes from "react-immutable-proptypes";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";

import PubSubTopics from "../../constants/pubSubTopics";
import timeoutRates from "../../constants/timeoutRates";
import { immutableRecords, PropTypeShapes } from "../../types";
// import debugUtils from "../../utils/debugUtils";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import { getEventIsTouch, getScreenPointerPositionFromEvent } from "../../utils/mouseUtils";
import uniqueId from "../../utils/uniqueId";
import { withContainerContext } from "../container/ContainerContext";
import ScrollBar from "./ScrollBar";
import { publishXScrollPosition, publishYScrollPosition, withScrollPositionContext } from "./ScrollPositionContext";
import { withZoomContext } from "./ZoomContext";
// import hasAncestorOfClass from "../../utils/hasAncestorOfClass";

// In future we may want to capture more things, and look at overflow and overflow-y and overflow-x generally
// to see if any of the ancestors are scrollable. But for now it is only dropdowns inside TableView we are wanting to
// protect, so this does the job by just looking for "dropdownMenu"...
// const scrollWasDropdownMenu = e => hasAncestorOfClass(e.target, "dropdownMenu");

const INFINITE_MARGIN = 20000;
const RECENTER_LIMIT = 3000;
const DRAG_SCROLL_MARGIN = 40;

const AUTO_SCROLL_UNIT_FOR_DRAG = 10;

const xScrollParams = [
  "left",
  "right",
  "width",
  "canvasLeft",
  "_scrollLeft",
  "horizontalIsInfinite",
  "_lastXScrollPosition"
];
const yScrollParams = [
  "top",
  "bottom",
  "height",
  "canvasTop",
  "_scrollTop",
  "verticalIsInfinite",
  "_lastYScrollPosition"
];

const scrollCoordsConstrainedByViewBounds = (viewBounds, viewportSize, scrollLeft, scrollTop, options) => {
  // And don't let it scroll outside the allowed viewBounds
  let finalLeft = scrollLeft;
  let finalTop = scrollTop;

  if (viewBounds) {
    if (!_.isNil(finalLeft) && options.scrollHorizontally) {
      const scrollRight = finalLeft + viewportSize.width;
      if (!_.isNil(viewBounds.right) && scrollRight > viewBounds.right) {
        finalLeft = viewBounds.right - viewportSize.width;
      }
      if (!_.isNil(viewBounds.left) && finalLeft < viewBounds.left) {
        finalLeft = viewBounds.left;
      }
    }
    if (!_.isNil(finalTop) && options.scrollVertically) {
      const scrollBottom = finalTop + viewportSize.height;
      if (!_.isNil(viewBounds.bottom) && scrollBottom > viewBounds.bottom) {
        finalTop = viewBounds.bottom - viewportSize.height;
      }
      if (!_.isNil(viewBounds.top) && finalTop < viewBounds.top) {
        finalTop = viewBounds.top;
      }
    }
  }
  return {
    left: !_.isNil(finalLeft) ? Math.round(finalLeft) : null,
    top: !_.isNil(finalTop) ? Math.round(finalTop) : null
  };
};

const getScrollXContextValue = memoizeOne((xScrollPosition, xScrollActions) => {
  return {
    xScrollPosition,
    xScrollActions
  };
});

const getScrollYContextValue = memoizeOne((yScrollPosition, yScrollActions) => {
  return {
    yScrollPosition,
    yScrollActions
  };
});

class ScrollableView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this._scrollInProgress = false;
    this._scrollLeft = props.initialScrollLeft ? Math.round(props.initialScrollLeft) : 0;
    this._scrollTop = props.initialScrollTop ? Math.round(props.initialScrollTop) : 0;

    // The last positions for the DOM elements scroll positions
    this._lastDomScrollLeft = 0;
    this._lastDomScrollTop = 0;
    this._lastDeltaX = 0;
    this._lastDeltaY = 0;
    this._viewBounds = null;

    this._isMounted = false;

    this._componentId = uniqueId();

    this._lastXScrollPosition = null;
    this._lastYScrollPosition = null;

    // The last positions sent to live listeners
    this._lastLiveLeft = null;
    this._lastLiveTop = null;

    this._animationInProgress = false;

    this.state = {
      scrollLeft: this._scrollLeft,
      scrollTop: this._scrollTop,
      canvasLeft: props.horizontalIsInfinite ? this._scrollLeft - INFINITE_MARGIN : 0,
      canvasTop: props.verticalIsInfinite ? this._scrollTop - INFINITE_MARGIN : 0,
      dropMessage: null
    };

    this._updateHorizontalScrollbar = null;
    this._updateVerticalScrollbar = null;
    this._liveListeners = [];

    // eslint-disable-next-line max-len
    this.registerHorizontalScrollbarUpdater = this.registerHorizontalScrollbarUpdater.bind(this);
    this.registerVerticalScrollbarUpdater = this.registerVerticalScrollbarUpdater.bind(this);
    this.registerLiveListener = this.registerLiveListener.bind(this);
    this.unregisterLiveListener = this.unregisterLiveListener.bind(this);
    this.scrollByAmount = this.scrollByAmount.bind(this);
    this.scrollTo = this.scrollTo.bind(this);
    this.scrollbarMoved = this.scrollbarMoved.bind(this);
    this.animatedScrollTo = this.animatedScrollTo.bind(this);
    this.animatedScrollRectIntoView = this.animatedScrollRectIntoView.bind(this);
    this.jumpToViewport = this.jumpToViewport.bind(this);
    this.jumpScrollRectIntoView = this.jumpScrollRectIntoView.bind(this);
    this.onDragStartedReceived = this.onDragStartedReceived.bind(this);
    this.onDragEndedReceived = this.onDragEndedReceived.bind(this);

    this.handleScroll = this.handleScroll.bind(this);
    this.dragPointerMove = this.dragPointerMove.bind(this);
    this.dragPointerLeave = this.dragPointerLeave.bind(this);
    this.handleZoomEventStart = this.handleZoomEventStart.bind(this);
    this.handleZoomEventChange = this.handleZoomEventChange.bind(this);
    this.handleZoomEventEnd = this.handleZoomEventEnd.bind(this);

    this.handleMouseWheel = this.handleMouseWheel.bind(this);
    this.handleWheelEvent = this.handleWheelEvent.bind(this);

    this.throttledHandleScroll = _.throttle(this.handleScroll, timeoutRates.SCROLL_THROTTLE, {
      leading: false,
      trailing: true
    });
    this.handleFirstScrollThenThrottle = this.handleFirstScrollThenThrottle.bind(this);

    this.throttledhandleZoomEventChange = _.throttle(this.handleZoomEventChange, timeoutRates.ZOOM_THROTTLE, {
      leading: false,
      trailing: true
    });

    this.throttledHandleMouseWheel = _.throttle(this.handleMouseWheel, timeoutRates.ZOOM_THROTTLE, {
      leading: false,
      trailing: true
    });

    this.liveScrollingStopped = this.liveScrollingStopped.bind(this);
    this.debouncedLiveScrollingStopped = _.debounce(this.liveScrollingStopped, timeoutRates.LIVE_SCROLL_TIMEOUT);

    this.saveScroll = this.saveScroll.bind(this);
    this.debouncedSaveScroll = _.debounce(this.saveScroll, timeoutRates.RECORD_SCROLL_TIMEOUT);

    this.transferLocalScrollToState = this.transferLocalScrollToState.bind(this);
    this.debouncedCenterWindow = _.debounce(
      () => this.transferLocalScrollToState(true),
      timeoutRates.RECENTER_SCROLL_TIMEOUT
    );

    this.scrollForDragIfNeeded = this.scrollForDragIfNeeded.bind(this);
    this.debouncedScrollForDragIfNeeded = _.debounce(this.scrollForDragIfNeeded, timeoutRates.SCROLL_THROTTLE);

    this.getScrollCanvas = this.getScrollCanvas.bind(this);
    this.getScrollPosition = this.getScrollPosition.bind(this);

    this.calculateZoomChange = this.calculateZoomChange.bind(this);

    this._scrollActions = {
      animateToViewport: this.animatedScrollTo,
      jumpToViewport: this.jumpToViewport,
      animateRectIntoView: this.animatedScrollRectIntoView,
      jumpRectIntoView: this.jumpScrollRectIntoView,
      registerForLiveUpdates: this.registerLiveListener,
      unregisterForLiveUpdates: this.unregisterLiveListener
    };

    this.getXScrollCanvas = () => this.getScrollCanvas(...xScrollParams);
    this.getYScrollCanvas = () => this.getScrollCanvas(...yScrollParams);
    this.getXScrollPosition = () => this.getScrollPosition(...xScrollParams);
    this.getYScrollPosition = () => this.getScrollPosition(...yScrollParams);
  }

  componentWillMount() {
    this.updateViewBounds(this.props);
  }

  componentDidMount() {
    this._isMounted = true;
    this.addListeners();
    this.updateViewBounds(this.props);
    this.transferLocalScrollToState(true);
    this._pubSubDragStartedToken = PubSub.subscribe(PubSubTopics.dragStarted, this.onDragStartedReceived);
    this._pubSubDragEndedToken = PubSub.subscribe(PubSubTopics.dragEnded, this.onDragEndedReceived);

    // Re-set the dom scroll - will also notify listeners if needed.
    this.setDomScroll();
  }

  componentWillReceiveProps(nextProps) {
    if (!_.isNil(nextProps.initialScrollLeft) && nextProps.initialScrollLeft !== this.props.initialScrollLeft) {
      this._scrollLeft = nextProps.initialScrollLeft ? Math.round(nextProps.initialScrollLeft) : 0;
    }
    if (!_.isNil(nextProps.initialScrollTop) && nextProps.initialScrollTop !== this.props.initialScrollTop) {
      this._scrollTop = nextProps.initialScrollTop ? Math.round(nextProps.initialScrollTop) : 0;
    }

    if (
      this.props.zoom !== nextProps.zoom ||
      this.props.children !== nextProps.children ||
      this.props.requestViewBoundsForZoom !== nextProps.requestViewBoundsForZoom
    ) {
      this.updateViewBounds(nextProps);
    }
    this.transferLocalScrollToState(true);
  }

  componentWillUpdate() {}

  componentDidUpdate() {
    // Re-set the dom scroll - will also notify listeners if needed.
    this.setDomScroll();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.removeListeners();
    this.debouncedLiveScrollingStopped.cancel();
    this.debouncedSaveScroll.cancel();
    this.debouncedCenterWindow.cancel();
    this.debouncedScrollForDragIfNeeded.cancel();
    this.throttledHandleScroll.cancel();
    this.throttledhandleZoomEventChange.cancel();

    PubSub.unsubscribe(this._pubSubDragStartedToken);
    PubSub.unsubscribe(this._pubSubDragEndedToken);
  }

  addListeners() {
    this._domNode.addEventListener("scroll", this.handleFirstScrollThenThrottle, false);
    this._domNode.addEventListener("gesturestart", this.handleZoomEventStart, false);
    this._domNode.addEventListener("gesturechange", this.throttledhandleZoomEventChange, false);
    this._domNode.addEventListener("gestureend", this.handleZoomEventEnd, false);
    const wheelEvent = "onwheel" in document.createElement("div") ? "wheel" : "mousewheel";
    this._domNode.addEventListener(wheelEvent, this.handleWheelEvent, false);
  }

  onDragStartedReceived(_msg, data) {
    if (data.isTouch) {
      this._domNode.addEventListener("touchmove", this.dragPointerMove, false);
    } else {
      this._domNode.addEventListener("mousemove", this.dragPointerMove, false);
      this._domNode.addEventListener("mouseleave", this.dragPointerLeave, false);
    }
  }

  onDragEndedReceived() {
    this._domNode.removeEventListener("touchmove", this.dragPointerMove, false);
    this._domNode.removeEventListener("mousemove", this.dragPointerMove, false);
    this._domNode.removeEventListener("mouseleave", this.dragPointerLeave, false);
    this._dragPointerX = null;
    this._dragPointerY = null;
  }

  removeListeners() {
    this._domNode.removeEventListener("scroll", this.handleFirstScrollThenThrottle, false);
    this._domNode.removeEventListener("gesturestart", this.handleZoomEventStart, false);
    this._domNode.removeEventListener("gesturechange", this.throttledhandleZoomEventChange, false);
    this._domNode.removeEventListener("gestureend", this.handleZoomEventEnd, false);
    this._domNode.removeEventListener("touchmove", this.dragPointerMove, false);
    this._domNode.removeEventListener("mousemove", this.dragPointerMove, false);
    this._domNode.removeEventListener("mouseleave", this.dragPointerLeave, false);
    const wheelEvent = "onwheel" in document.createElement("div") ? "wheel" : "mousewheel";
    this._domNode.removeEventListener(wheelEvent, this.handleWheelEvent, false);
  }

  registerHorizontalScrollbarUpdater(f) {
    this._updateHorizontalScrollbar = f;
  }

  registerVerticalScrollbarUpdater(f) {
    this._updateVerticalScrollbar = f;
  }

  registerLiveListener(listener) {
    this._liveListeners.push(listener);
    this.notifyNewListener(listener);
  }

  notifyNewListener(listener) {
    let left = null;
    let top = null;
    if (this.props.scrollHorizontally) {
      const scrollPosition = this.getXScrollPosition();
      left = scrollPosition.absoluteScroll;
    }
    if (this.props.scrollVertically) {
      const scrollPosition = this.getYScrollPosition();
      top = scrollPosition.absoluteScroll;
    }
    if (listener.onLiveScrollUpdate) {
      listener.onLiveScrollUpdate(left, top);
    }
  }

  unregisterLiveListener(listeners) {
    _.pull(this._liveListeners, listeners);
  }

  updateViewBounds(useProps) {
    this._viewBounds = this.props.requestViewBoundsForZoom ? useProps.requestViewBoundsForZoom(useProps.zoom) : null;
    // If we did not get a bounds, there is nothing to check
    if (!this._viewBounds || this._scrollInProgress) {
      return;
    }
    // Since the viewBounds has updated, we need to make sure our current scroll position is not outside those limits
    if (this.props.scrollHorizontally) {
      if (!_.isNil(this._viewBounds.right)) {
        const containerSize = useProps.containerRect.width;
        this._scrollLeft = Math.min(this._scrollLeft, this._viewBounds.right - containerSize);
      }
      if (!_.isNil(this._viewBounds.left)) {
        this._scrollLeft = Math.max(this._scrollLeft, this._viewBounds.left);
      }
    }
    if (this.props.scrollVertically) {
      if (!_.isNil(this._viewBounds.bottom)) {
        const containerSize = useProps.containerRect.height;
        this._scrollTop = Math.min(this._scrollTop, this._viewBounds.bottom - containerSize);
      }
      if (!_.isNil(this._viewBounds.top)) {
        this._scrollTop = Math.max(this._scrollTop, this._viewBounds.top);
      }
    }
  }

  handleFirstScrollThenThrottle(e) {
    if (this.props.disableCtrlScroll) {
      const modifierKeys = getModifierKeysForEvent(e);
      if (modifierKeys.CONTROL || modifierKeys.COMMAND) {
        e.preventDefault();
        return;
      }
    }

    e.preventDefault();
    if (!this._scrollInProgress) {
      this.handleScroll(e);
      return;
    }
    this.throttledHandleScroll(e);
  }

  handleScroll(e) {
    let changed = false;
    let newLeft = null;
    let newTop = null;
    if (this.props.scrollHorizontally) {
      const canvas = this.getXScrollCanvas();
      newLeft = Math.round(canvas.min + e.target.scrollLeft);
      if (newLeft !== this._scrollLeft) {
        this._lastDomScrollLeft = newLeft;
        changed = true;
      }
    }
    if (this.props.scrollVertically) {
      const canvas = this.getYScrollCanvas();
      newTop = Math.round(canvas.min + e.target.scrollTop);
      if (newTop !== this._scrollTop) {
        changed = true;
        this._lastDomScrollTop = newTop;
      }
    }

    if (changed) {
      this.updateZoomAndScroll(null, newLeft, newTop);
    }
  }

  handleWheelEvent(e) {
    // Have to prevent default before throttling, otherwise it scrolls the view
    if (this.props.disableCtrlScroll) {
      const modifierKeys = getModifierKeysForEvent(e);
      if (modifierKeys.CONTROL || modifierKeys.COMMAND) {
        e.preventDefault();
        return;
      }
    }
    this.throttledHandleMouseWheel(e);
  }

  handleMouseWheel(e) {
    if (this.props.allowCtrlScrollZoom) {
      const modifierKeys = getModifierKeysForEvent(e);
      if (modifierKeys.CONTROL || modifierKeys.COMMAND) {
        let amountScrolled = e.deltaY;

        if (amountScrolled < 0) {
          amountScrolled = Math.max(Math.min(-5, amountScrolled), -250);
        }
        if (amountScrolled > 0) {
          amountScrolled = Math.max(Math.min(250, amountScrolled), 5);
        }

        const newZoom = 1 + amountScrolled / 250;
        const event = {
          preventDefault: () => {},
          pageX: e.pageX,
          pageY: e.pageY,
          scale: newZoom
        };
        this.handleZoomEventStart(event);

        this._applyScale = newZoom;
        this.calculateZoomChange();
      }
    }
  }

  dragPointerMove(e) {
    const isTouch = getEventIsTouch(e);
    const pos = getScreenPointerPositionFromEvent(e);
    const domRect = this._domNode.getBoundingClientRect();
    const pointerX = pos.x - domRect.left;
    const pointerY = pos.y - domRect.top;
    this._dragPointerX = pointerX;
    this._dragPointerY = pointerY;

    const r = this.props.containerRect;
    const insideView = pos.x >= r.left && pos.x <= r.right && pos.y >= r.top && pos.y <= r.bottom;

    if (isTouch && !insideView) {
      this.dragPointerLeave();
    } else {
      this.scrollForDragIfNeeded();
    }
  }

  dragPointerLeave(_e) {
    this._dragPointerX = null;
    this._dragPointerY = null;
    this.debouncedScrollForDragIfNeeded.cancel();
  }

  scrollForDragIfNeeded() {
    this.debouncedScrollForDragIfNeeded.cancel();
    if (!this._dragPointerX || !this._dragPointerY) {
      return;
    }
    let scrollX = 0;
    let scrollY = 0;
    if (this.props.scrollHorizontally) {
      scrollX =
        this._dragPointerX < DRAG_SCROLL_MARGIN
          ? -AUTO_SCROLL_UNIT_FOR_DRAG
          : this._dragPointerX > this.props.containerRect.width - DRAG_SCROLL_MARGIN
            ? AUTO_SCROLL_UNIT_FOR_DRAG
            : 0;
    }
    if (this.props.scrollVertically) {
      scrollY =
        this._dragPointerY < DRAG_SCROLL_MARGIN
          ? -AUTO_SCROLL_UNIT_FOR_DRAG
          : this._dragPointerY > this.props.containerRect.height - DRAG_SCROLL_MARGIN
            ? AUTO_SCROLL_UNIT_FOR_DRAG
            : 0;
    }

    if (scrollX || scrollY) {
      this.scrollByAmount(scrollX, scrollY);
      PubSub.publish(PubSubTopics.dragAutoscroll);
      this.debouncedScrollForDragIfNeeded();
    }
  }

  handleZoomEventStart(e) {
    e.preventDefault();
    if (!this.props.scrollHorizontally) {
      return;
    }
    this._gestureStartZoom = this.props.zoom;
    // Where is the mouse relative to this element.
    // What timestamp is the mouse at?
    const pageMouseX = e.pageX;
    const pageMouseY = e.pageY;

    const domRect = this._domNode.getBoundingClientRect();
    const mouseX = pageMouseX - domRect.left;
    this._gestureStartMouseX = mouseX;
    this._gestureStartScaledMouseX = (this._scrollLeft + this._gestureStartMouseX) * this._gestureStartZoom;

    const mouseY = pageMouseY - domRect.top;
    this._gestureStartMouseY = mouseY;
    this._gestureStartScaledMouseY = (this._scrollTop + this._gestureStartMouseY) * this._gestureStartZoom;
    this._applyScale = e.scale;
  }

  handleZoomEventChange(e) {
    e.preventDefault();
    if (!this.props.scrollHorizontally) {
      return;
    }
    if (e.scale) {
      this._applyScale = e.scale;
    }
    this.calculateZoomChange();
  }

  calculateZoomChange() {
    const scale = this._applyScale;
    if (!this.props.allowPinchZoom) {
      return;
    }
    const initialZoom = this.props.zoom;
    let newZoom = initialZoom;
    // Adjust the scale
    if (this.props.invertZoom) {
      newZoom = this._gestureStartZoom / scale;
    } else {
      newZoom = this._gestureStartZoom * scale;
    }

    // Don't allow scale beyond min/max bounds
    if (!_.isNil(this.props.zoomMinimum)) {
      if (newZoom < this.props.zoomMinimum) {
        newZoom = this.props.zoomMinimum;
      }
    }
    if (!_.isNil(this.props.zoomMaximum)) {
      if (newZoom > this.props.zoomMaximum) {
        newZoom = this.props.zoomMaximum;
      }
    }

    // If after all that nothing has changed, don't bother sending a notification or anything.
    if (newZoom === initialZoom) {
      return;
    }

    // If we can scroll horizontally, then we should keep the view centered where the mouse is
    if (this.props.scrollHorizontally) {
      // Now adjust _scrollLeft to keep the pixel the same at the mouse position
      const scaledLeft = this._gestureStartScaledMouseX - this._gestureStartMouseX * newZoom;
      const left = scaledLeft / newZoom;
      this.updateZoomAndScroll(newZoom, left, null);
    } else {
      this.props.onZoomChange(newZoom);
    }
  }

  handleZoomEventEnd(e) {
    e.preventDefault();
  }

  scrollByAmount(deltaX, deltaY) {
    // Adjust the local scroll position immediately
    const proposedLeft = this.props.scrollHorizontally ? this._scrollLeft + deltaX : this._scrollLeft;
    const proposedTop = this.props.scrollVertically ? this._scrollTop + deltaY : this._scrollTop;

    // Force it back to within view bounds as required
    const pos = scrollCoordsConstrainedByViewBounds(
      this._viewBounds,
      this.props.containerRect,
      proposedLeft,
      proposedTop,
      this.props
    );
    const left = Math.round(pos.left);
    const top = Math.round(pos.top);
    this.updateZoomAndScroll(null, left, top);
  }

  scrollbarMoved(scrollLeft, scrollTop) {
    const pos = scrollCoordsConstrainedByViewBounds(
      this._viewBounds,
      this.props.containerRect,
      scrollLeft,
      scrollTop,
      this.props
    );
    this.scrollTo(pos.left, pos.top);
  }

  scrollTo(scrollLeft, scrollTop) {
    this.updateZoomAndScroll(null, scrollLeft, scrollTop);
  }

  animatedScrollRectIntoView(r) {
    // First, check if the rect is already entirely in the viewport.
    // If it is, no need to scroll.
    const viewport = {
      left: this._scrollLeft,
      top: this._scrollTop,
      width: this.props.containerRect.width,
      height: this.props.containerRect.height,
      right: this._scrollLeft + this.props.containerRect.width,
      bottom: this._scrollTop + this.props.containerRect.height
    };

    // Does this view need to scroll?
    let left = null;
    let top = null;
    if (this.props.scrollHorizontally) {
      if (r.left > viewport.right || r.right < viewport.left) {
        // Let's aim to center the item in the view...
        // we want to make {cx,cy} the center of our view
        const cx = (r.left + r.right) / 2;
        left = this.props.scrollHorizontally ? cx - this.props.containerRect.width / 2 : null;
      }
    }
    if (this.props.scrollVertically) {
      if (r.top > viewport.bottom || r.bottom < viewport.top) {
        // Let's aim to center the item in the view...
        // we want to make {cx,cy} the center of our view
        const cy = (r.top + r.bottom) / 2;
        top = this.props.scrollVertically ? cy - this.props.containerRect.height / 2 : null;
      }
    }

    // If there is no scroll required that we can handle, bail now
    if (_.isNil(left) && _.isNil(top)) {
      return;
    }
    this.animatedScrollTo(left, top);
  }

  jumpScrollRectIntoView(r) {
    // First, check if the rect is already entirely in the viewport.
    // If it is, no need to scroll.
    const viewport = {
      left: this._scrollLeft,
      top: this._scrollTop,
      width: this.props.containerRect.width,
      height: this.props.containerRect.height,
      right: this._scrollLeft + this.props.containerRect.width,
      bottom: this._scrollTop + this.props.containerRect.height
    };

    let insideVertical = false;
    let insideHorizontal = false;

    // Unless the rect is wider than the viewport, let's make sure both ends are in view...
    if (viewport.width > r.width) {
      // The item is narrower than viewport, so make sure it is entirely within view
      if (r.left >= viewport.left && r.right <= viewport.right) {
        insideHorizontal = true;
      }
    } else {
      // The item is wider, so just make sure that some is in view
      if (r.left < viewport.right && r.right > viewport.left) {
        insideHorizontal = true;
      }
    }
    if (r.top >= viewport.top && r.bottom <= viewport.bottom) {
      insideVertical = true;
    }

    // The rect is already visible within both, so no need to scroll
    if (insideVertical && insideHorizontal) {
      return;
    }

    // If we reach here, we need to scroll in at least one dimension
    // Adjust so the edge of the item is the egde of our screen
    const left = insideHorizontal
      ? this._scrollLeft
      : r.right > viewport.right
        ? r.right - this.props.containerRect.width
        : r.left;
    const top = insideVertical
      ? this._scrollTop
      : r.bottom > viewport.bottom
        ? r.bottom - this.props.containerRect.height
        : r.top;

    const endPos = scrollCoordsConstrainedByViewBounds(
      this._viewBounds,
      this.props.containerRect,
      left,
      top,
      this.props
    );

    this.scrollTo(endPos.left, endPos.top);
  }

  animatedScrollTo(scrollLeft, scrollTop) {
    const left = _.isNil(scrollLeft) || !this.props.scrollHorizontally ? this._scrollLeft : scrollLeft;
    const top = _.isNil(scrollTop) || !this.props.scrollVertically ? this._scrollTop : scrollTop;
    let startPos = { left: this._scrollLeft, top: this._scrollTop };
    let endPos = scrollCoordsConstrainedByViewBounds(this._viewBounds, this.props.containerRect, left, top, this.props);

    this._animationInProgress = true;
    dynamics.animate(startPos, endPos, {
      type: dynamics.easeInOut,
      change: (obj, _progress) => {
        if (this._isMounted) {
          this.scrollTo(obj.left, obj.top);
        }
      },
      complete: () => {
        if (this._isMounted) {
          this._animationInProgress = false;
        }
      }
    });
  }

  jumpToViewport(left, top, zoom) {
    this.updateZoomAndScroll(zoom, left, top);
  }

  setDomScroll() {
    if (!this._domNode) {
      return;
    }
    let leftForListeners = null;
    let topForListeners = null;
    let resetRequired = false;
    let hasChanged = false;
    if (this.props.scrollHorizontally) {
      const scrollPosition = this.getXScrollPosition();
      if (this._lastDomScrollLeft !== scrollPosition.relativeScroll) {
        const maxLimit = scrollPosition.canvas.range - this.props.containerRect.width;
        if (scrollPosition.relativeScroll < 0 || scrollPosition.relativeScroll > maxLimit) {
          resetRequired = true;
        } else {
          this._domNode.scrollLeft = scrollPosition.relativeScroll;
          this._lastDomScrollLeft = scrollPosition.relativeScroll;
        }
      }
      if (this._lastLiveLeft !== scrollPosition.absoluteScroll) {
        hasChanged = true;
      }
      leftForListeners = scrollPosition.absoluteScroll;
    }
    if (this.props.scrollVertically) {
      const scrollPosition = this.getYScrollPosition();
      if (this._lastDomScrollTop !== scrollPosition.relativeScroll) {
        const maxLimit = scrollPosition.canvas.range - this.props.containerRect.height;
        if (scrollPosition.relativeScroll < 0 || scrollPosition.relativeScroll > maxLimit) {
          resetRequired = true;
        } else {
          this._domNode.scrollTop = scrollPosition.relativeScroll;
          this._lastDomScrollTop = scrollPosition.relativeScroll;
        }
      }
      if (this._lastLiveTop !== scrollPosition.absoluteScroll) {
        hasChanged = true;
      }
      topForListeners = scrollPosition.absoluteScroll;
    }

    if (resetRequired) {
      this.transferLocalScrollToState(false);
      return;
    }

    if (hasChanged) {
      this.notifyLiveListeners(leftForListeners, topForListeners);
    }
  }

  notifyLiveListeners(left, top) {
    const isStartingScroll = !this._scrollInProgress;
    this._scrollInProgress = true;
    this._lastLiveLeft = left;
    this._lastLiveTop = top;
    this._liveListeners.forEach(listener => {
      if (isStartingScroll && listener.onLiveScrollStart) {
        listener.onLiveScrollStart(
          this.props.scrollHorizontally,
          this.props.scrollVertically,
          this._animationInProgress
        );
      }
      if (listener.onLiveScrollUpdate) {
        listener.onLiveScrollUpdate(left, top, this._animationInProgress);
      }
    });
    this.debouncedLiveScrollingStopped();
  }

  liveScrollingStopped() {
    const wasScrolling = this._scrollInProgress;
    this._scrollInProgress = false;

    if (wasScrolling) {
      this._liveListeners.forEach(listener => {
        if (listener.onLiveScrollStop) {
          listener.onLiveScrollStop(this.props.scrollHorizontally, this.props.scrollVertically);
        }
      });
    }
  }

  updateZoomAndScroll(zoom, scrollLeft, scrollTop) {
    let resetCanvas = false;
    let recenterCanvas = false;

    // If we have infinite scrolling, have we reached the edge of the scroll window so need to render again?
    // Or, if we have non-infinite scrolling, has our canvas size changed?
    if (this.props.scrollHorizontally && this.props.horizontalIsInfinite && !_.isNil(scrollLeft)) {
      const canvasMin = this.state.canvasLeft;
      const canvasMax = canvasMin + INFINITE_MARGIN * 2;
      const viewportMax = scrollLeft + this.props.containerRect.width;
      const distFromMin = scrollLeft - canvasMin;
      const distFromMax = canvasMax - viewportMax;
      if (distFromMin < RECENTER_LIMIT || distFromMax < RECENTER_LIMIT) {
        resetCanvas = true;
        recenterCanvas = true;
      }
    }
    if (this.props.scrollVertically && this.props.verticalIsInfinite && !_.isNil(scrollTop)) {
      const canvasMin = this.state.canvasTop;
      const canvasMax = canvasMin + INFINITE_MARGIN * 2;
      const viewportMax = scrollTop + this.props.containerRect.height;
      const distFromMin = scrollTop - canvasMin;
      const distFromMax = canvasMax - viewportMax;
      if (distFromMin < RECENTER_LIMIT || distFromMax < RECENTER_LIMIT) {
        resetCanvas = true;
        recenterCanvas = true;
      }
    }

    if (
      (_.isNil(scrollLeft) || scrollLeft === this._scrollLeft) &&
      (_.isNil(scrollTop) || scrollTop === this._scrollTop) &&
      (_.isNil(zoom) || zoom === this.props.zoom)
    ) {
      if (resetCanvas) {
        this.transferLocalScrollToState(recenterCanvas);
      }
      return;
    }

    // Update scrollLeft and scrollTop
    if (!_.isNil(scrollLeft)) {
      this._scrollLeft = Math.round(scrollLeft);
    }
    if (!_.isNil(scrollTop)) {
      this._scrollTop = Math.round(scrollTop);
    }

    // If the zoom has changed, then we just need to pass it up to ZoomContext
    // and wait for the prop to filter back down...
    // That will trigger a complete re-render and then we will be golden.
    if (!_.isNil(zoom) && zoom !== this.props.zoom && this.props.onZoomChange) {
      this.props.onZoomChange(zoom);
      // And schedule this to be saved to our data store.
      this.debouncedSaveScroll();
      return;
    }

    // Otherwise, update scroll position
    const diffLeft = this.state.scrollLeft - this._scrollLeft;
    const diffTop = this.state.scrollTop - this._scrollTop;

    if (!this._viewBounds) {
      this._viewBounds = this.props.requestViewBoundsForZoom
        ? this.props.requestViewBoundsForZoom(this.props.zoom)
        : null;
    }

    this.setDomScroll();

    const reRenderLimit = this.props.preRenderMargin / 2;

    if (!resetCanvas && Math.abs(diffLeft) < reRenderLimit && Math.abs(diffTop) < reRenderLimit) {
      if (this._updateHorizontalScrollbar) {
        this._updateHorizontalScrollbar(this.getViewportForScrollBars());
      }
      if (this._updateVerticalScrollbar) {
        this._updateVerticalScrollbar(this.getViewportForScrollBars());
      }
    } else {
      this.transferLocalScrollToState(recenterCanvas);
    }

    // And finally, schedule this to be saved to our data store.
    this.debouncedSaveScroll();

    // And schedule infinite views to re-center if they need to
    if (this.props.horizontalIsInfinite || this.props.verticalIsInfinite) {
      this.debouncedCenterWindow();
    }
  }

  saveScroll() {
    if (this.props.onScrollChange) {
      this.props.onScrollChange(
        immutableRecords.PointRecord({
          x: this._scrollLeft,
          y: this._scrollTop
        }),
        this.props.zoom
      );
    }
  }

  transferLocalScrollToState(recenterCanvas) {
    if (!this._isMounted) {
      return;
    }
    let newState = {
      scrollLeft: this._scrollLeft,
      scrollTop: this._scrollTop
    };

    if (this.props.horizontalIsInfinite && recenterCanvas) {
      newState.canvasLeft = this._scrollLeft - INFINITE_MARGIN;
    }
    if (this.props.verticalIsInfinite && recenterCanvas) {
      newState.canvasTop = this._scrollTop - INFINITE_MARGIN;
    }
    this.setState(newState);
  }

  getViewportForScrollBars() {
    return immutableRecords.FlatRectRecord({
      left: this._scrollLeft,
      top: this._scrollTop,
      width: this.props.containerRect.width,
      height: this.props.containerRect.height,
      right: this._scrollLeft + this.props.containerRect.width,
      bottom: this._scrollTop + this.props.containerRect.height
    });
  }

  getScrollCanvas(minProp, maxProp, rangeProp, canvasProp, thisScrollProp, isInfinite) {
    // Container gives the dimension of the scroll container we are showing this in
    const containerSize = this.props.containerRect.get(rangeProp);
    // Canvas gives the total size of the view within the scroll container
    let canvasMin = null;
    let canvasMax = null;

    if (this.props[isInfinite]) {
      const viewBoundsMin = this._viewBounds ? this._viewBounds[minProp] : null;
      const viewBoundsMax = this._viewBounds ? this._viewBounds[maxProp] : null;
      canvasMin = this.state[canvasProp];
      if (!_.isNil(viewBoundsMin)) {
        canvasMin = Math.max(canvasMin, viewBoundsMin);
      }
      canvasMax = canvasMin + INFINITE_MARGIN * 2;
      if (!_.isNil(viewBoundsMax)) {
        canvasMax = Math.min(canvasMax, viewBoundsMax);
      }
    } else {
      canvasMin = this._viewBounds ? this._viewBounds[minProp] : 0;
      canvasMax = Math.max(this._viewBounds ? this._viewBounds[maxProp] : 0, canvasMin + containerSize);
    }

    return immutableRecords.RangeRecord({
      min: canvasMin,
      max: canvasMax,
      range: canvasMax - canvasMin
    });
  }

  getScrollPosition(minProp, maxProp, rangeProp, canvasProp, thisScrollProp, isInfinite, cacheName) {
    // Container gives the dimension of the scroll container we are showing this in
    const containerSize = this.props.containerRect.get(rangeProp);

    // Get the canvas from above
    const canvas = this.getScrollCanvas(minProp, maxProp, rangeProp, canvasProp, thisScrollProp, isInfinite);

    // Viewport gives the current field of view of the container, plus any overscroll amount we want to include
    const viewportMin = Math.max(canvas.min, this[thisScrollProp] - this.props.preRenderMargin);

    const viewportMax = Math.min(canvas.max, viewportMin + containerSize + this.props.preRenderMargin * 2);

    const viewportRange = viewportMax - viewportMin;

    const viewport = immutableRecords.RangeRecord({
      min: viewportMin,
      max: viewportMax,
      range: viewportRange
    });

    const absoluteScroll = this[thisScrollProp];
    const relativeScroll = absoluteScroll - canvas.min;

    const result = immutableRecords.ScrollPositionRecord({
      viewport,
      canvas,
      absoluteScroll,
      relativeScroll,
      zoom: this.props.zoom
    });

    if (this[cacheName] && _.isEqual(result.toJS(), this[cacheName].toJS())) {
      return this[cacheName];
    }

    this[cacheName] = result;

    return result;
  }

  render() {
    const scrollbarViewport = this.getViewportForScrollBars();
    const className = `scrollableView ${this.props.scrollHorizontally ? "horizontal" : ""} ${
      this.props.scrollVertically ? "vertical" : ""
    }`;
    let viewStyle = {};
    let canvasStyle = {};

    let scrollBars = [];

    let canvasChildren = this.props.children;

    if (this.props.scrollHorizontally) {
      // Get the X scroll details
      const xScrollPosition = this.getXScrollPosition();
      // Update the view style and canvas style
      viewStyle.width = `${this.props.containerRect.width}px`;
      canvasStyle.width = `${xScrollPosition.canvas.range}px`;
      // And wrap the child in a ScrollX.Provider
      canvasChildren = publishXScrollPosition(
        canvasChildren,
        getScrollXContextValue(xScrollPosition, this._scrollActions)
      );
      // Finally, do we need to show scrollbars?
      if (this.props.showHorizontalScrollbar) {
        scrollBars.push(
          <ScrollBar
            key="horizontalScrollbar"
            horizontal={true}
            containerRect={this.props.containerRect}
            paddingLeft={this.props.paddingLeft}
            paddingRight={this.props.paddingRight}
            viewRect={this._viewBounds}
            viewportRect={scrollbarViewport}
            onScroll={value => this.scrollbarMoved(value, null)}
            registerUpdateFunctionWithScrollableView={this.registerHorizontalScrollbarUpdater}
          />
        );
      }
    } else if (this.props.xScrollPosition) {
      canvasStyle.width = `${this.props.xScrollPosition.canvas.range}px`;
    } else {
      canvasStyle.width = `${this.props.containerRect.width}px`;
    }

    if (this.props.scrollVertically) {
      // Get the Y scroll details
      const yScrollPosition = this.getYScrollPosition();
      // Update the view style and canvas style
      viewStyle.height = `${this.props.containerRect.height}px`;
      canvasStyle.height = `${yScrollPosition.canvas.range}px`;
      // And wrap the child in a ScrollY.Provider
      canvasChildren = publishYScrollPosition(
        canvasChildren,
        getScrollYContextValue(yScrollPosition, this._scrollActions)
      );
      // Finally, do we need to show scrollbars?
      if (this.props.showVerticalScrollbar) {
        scrollBars.push(
          <ScrollBar
            key="verticalScrollbar"
            horizontal={false}
            containerRect={this.props.containerRect}
            paddingLeft={this.props.paddingLeft}
            paddingRight={this.props.paddingRight}
            viewRect={this._viewBounds}
            viewportRect={scrollbarViewport}
            onScroll={value => this.scrollbarMoved(null, value)}
            registerUpdateFunctionWithScrollableView={this.registerVerticalScrollbarUpdater}
          />
        );
      }
    } else if (this.props.yScrollPosition) {
      canvasStyle.height = `${this.props.yScrollPosition.canvas.range}px`;
    } else {
      canvasStyle.height = `${this.props.containerRect.height}px`;
    }

    let result = (
      <div className={className} style={viewStyle} ref={node => (this._domNode = node)}>
        <div
          key="scrollableCanvas"
          className="scrollableCanvas"
          style={canvasStyle}
          ref={node => (this._canvasNode = node)}
        >
          {canvasChildren}
        </div>
        {scrollBars.length ? scrollBars : null}
      </div>
    );
    return result;
  }
}

ScrollableView.propTypes = {
  children: PropTypes.element.isRequired,
  scrollHorizontally: PropTypes.bool,
  scrollVertically: PropTypes.bool,
  singleDirectionScroll: PropTypes.bool,
  allowPinchZoom: PropTypes.bool,
  invertZoom: PropTypes.bool,
  horizontalIsInfinite: PropTypes.bool,
  verticalIsInfinite: PropTypes.bool,
  showVerticalScrollbar: PropTypes.bool,
  showHorizontalScrollbar: PropTypes.bool,
  allowCtrlScrollZoom: PropTypes.bool,
  disableCtrlScroll: PropTypes.bool,
  preRenderMargin: PropTypes.number,

  // Starting scroll positions
  initialScrollLeft: PropTypes.number,
  initialScrollTop: PropTypes.number,
  initialZoom: PropTypes.number,

  // Comes from ZoomContext
  zoom: PropTypes.number,
  zoomMinimum: PropTypes.number,
  zoomMaximum: PropTypes.number,
  onZoomChange: PropTypes.func,

  // Called after a scroll or zoom change has finished.
  onScrollChange: PropTypes.func,

  // Required if we are to draw scrollbars
  // Whenever the zoom changes, the app requests a new bounds
  requestViewBoundsForZoom: PropTypes.func,

  // Comes from ContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,

  // Scroll Position Context... from a parent scroll position
  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord),
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord)
};

export default withScrollPositionContext(withZoomContext(withContainerContext(ScrollableView)));
